import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AerodromeDetail,
  AirspaceShape,
  DataSourceStatus,
  FlightPlanFigures,
  LoadDataInput,
  PlanAircraftFigures,
  PlanLegInput,
  ReportingPointProperties,
  SavePlanInput,
  SpeedUnit,
  WaypointInput,
  WaypointType,
} from '@jupi/shared'
import { ApiError, api } from '@jupi/ui'

// The flight planner's server reads. Waypoints and aircraft change rarely,
// so they stay fresh for a while: a pilot at an airfield with one bar of
// signal should not be refetching them on every page.

export interface PlanWaypoint {
  code: string
  waypointType: WaypointType
  name: string | null
  lat: number
  lon: number
  variation: number | null
  com: string | null
  gnd: string | null
  atis: string | null
  area: string | null
  vor: string | null
  country: string
  link: string | null
  // The aerodrome a visual reporting point belongs to; null for others.
  aerodromeCode: string | null
  ownerUserId: string | null
  isShared: boolean
  distanceNm: number | null
}

export interface SavedRouteSummary {
  routeId: number
  name: string
  createdAt: string
  updatedAt: string
  pointCount: number
  summary: string
}

export interface SavedRoutePoint {
  position: number
  waypointCode: string | null
  code: string
  name: string | null
  lat: number | null
  lon: number | null
  variation: number | null
  com: string | null
  gnd: string | null
  atis: string | null
  area: string | null
  vor: string | null
  country: string | null
  link: string | null
}

export interface PlanAircraft {
  registration: string
  aircraftType: string | null
  ownerUserId: string | null
  mtow: number | null
  fuelType: string
  fuelConsumptionUsg: number | null
  speedUnit: SpeedUnit | null
  cruiseSpeed: number | null
  manualCruiseSpeed: number | null
  crosswindLimitKt: number | null
  hasPerformance: boolean
}

export interface Country {
  code: string
  nameEn: string
  nameFi: string
  nameSv: string
}

export interface WaypointFilter {
  q?: string
  country?: string
  own?: boolean
  near?: { lat: number; lon: number }
}

export const planKeys = {
  waypoints: (filter: WaypointFilter) => ['plan', 'waypoints', filter] as const,
  routes: ['plan', 'routes'] as const,
  route: (routeId: number) => ['plan', 'routes', routeId] as const,
  aircraft: ['plan', 'aircraft'] as const,
  countries: ['plan', 'countries'] as const,
  current: ['plan', 'current'] as const,
  reportingPoints: ['plan', 'reportingPoints'] as const,
  aerodrome: (code: string) => ['plan', 'aerodrome', code] as const,
  dataSources: ['plan', 'dataSources'] as const,
}

export function useWaypoints(filter: WaypointFilter = {}) {
  const search = new URLSearchParams()
  if (filter.q) search.set('q', filter.q)
  if (filter.country) search.set('country', filter.country)
  if (filter.own) search.set('own', '1')
  if (filter.near) {
    search.set('lat', String(filter.near.lat))
    search.set('lon', String(filter.near.lon))
  }
  return useQuery({
    queryKey: planKeys.waypoints(filter),
    queryFn: () => api.get<{ waypoints: PlanWaypoint[] }>(`/api/plan/waypoints?${search}`).then((r) => r.waypoints),
    // Typing a search should not blank the list between keystrokes.
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  })
}

export interface ReportingPoint extends ReportingPointProperties {
  lat: number
  lon: number
}

// The ENR 4.4 points from a static file (db/generate/airspace, refreshed
// with the airspace every AIRAC cycle), and the VRPs from the API: they are
// aerodrome data now (#81), loaded from lentopaikat.fi and the AIP, and
// change without a deploy.
export function useReportingPoints(enabled = true) {
  return useQuery({
    queryKey: planKeys.reportingPoints,
    queryFn: async () => {
      type Collection = {
        features: { properties: ReportingPointProperties; geometry: { coordinates: [number, number] } }[]
      }
      const res = await fetch('/map/reporting-points.geojson')
      if (!res.ok) throw new Error(`reporting points: HTTP ${res.status}`)
      const [statics, vrps] = await Promise.all([res.json() as Promise<Collection>, api.get<Collection>('/api/plan/vrps')])
      return [...statics.features.filter((f) => f.properties.kind !== 'vrp'), ...vrps.features].map(
        (f): ReportingPoint => ({ ...f.properties, lon: f.geometry.coordinates[0], lat: f.geometry.coordinates[1] }),
      )
    },
    enabled,
    staleTime: 10 * 60_000,
  })
}

// Runways, frequencies, links and VRPs of one aerodrome; null when the
// aerodrome has no details loaded.
export function useAerodrome(code: string | null) {
  return useQuery({
    queryKey: planKeys.aerodrome(code ?? ''),
    enabled: code !== null,
    queryFn: async () => {
      try {
        return (await api.get<{ aerodrome: AerodromeDetail }>(`/api/plan/aerodromes/${encodeURIComponent(code!)}`)).aerodrome
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null
        throw err
      }
    },
    staleTime: 10 * 60_000,
  })
}

// The static airspace file the map draws, for the pilot log sheet's "what
// do I enter and who do I call". Refreshed with every AIRAC cycle, so one
// fetch per session is plenty; at an airfield with no signal the service
// worker's copy, or none, answers and the sheet says so.
export function useAirspaceShapes(enabled: boolean) {
  return useQuery({
    queryKey: ['plan', 'airspaceShapes'] as const,
    enabled,
    staleTime: Infinity,
    queryFn: async () => {
      const response = await fetch('/map/airspace.geojson')
      if (!response.ok) throw new Error(`airspace.geojson ${response.status}`)
      return ((await response.json()) as { features: AirspaceShape[] }).features
    },
  })
}

// Which AIP edition the data is from, and when it was last checked.
export function useDataSources() {
  return useQuery({
    queryKey: planKeys.dataSources,
    queryFn: () => api.get<{ sources: DataSourceStatus[] }>('/api/plan/data-sources').then((r) => r.sources),
    staleTime: 10 * 60_000,
  })
}

export function useSavedRoutes() {
  return useQuery({
    queryKey: planKeys.routes,
    queryFn: () => api.get<{ routes: SavedRouteSummary[] }>('/api/plan/routes').then((r) => r.routes),
  })
}

export function useSavedRoute(routeId: number | null) {
  return useQuery({
    queryKey: planKeys.route(routeId ?? 0),
    enabled: routeId !== null,
    queryFn: () =>
      api
        .get<{ route: SavedRouteSummary & { points: SavedRoutePoint[] } }>(`/api/plan/routes/${routeId}`)
        .then((r) => r.route),
  })
}

export function usePlanAircraft() {
  return useQuery({
    queryKey: planKeys.aircraft,
    queryFn: () => api.get<{ aircraft: PlanAircraft[] }>('/api/plan/aircraft').then((r) => r.aircraft),
    staleTime: 10 * 60_000,
  })
}

export function useCountries() {
  return useQuery({
    queryKey: planKeys.countries,
    queryFn: () => api.get<{ countries: Country[] }>('/api/plan/countries').then((r) => r.countries),
    staleTime: Infinity,
  })
}

// --- The current plan -------------------------------------------------------

export interface StoredLeg extends PlanLegInput {
  position: number
  lat: number | null
  lon: number | null
}

export interface CurrentPlan {
  plan: Omit<SavePlanInput, 'legs'> & { updatedAt: string | null; legs: StoredLeg[] }
  load: LoadDataInput
  cruiseOverrides: Record<string, number>
  aircraft: PlanAircraftFigures | null
  computed: FlightPlanFigures
}

export function useCurrentPlan() {
  return useQuery({
    queryKey: planKeys.current,
    queryFn: () => api.get<CurrentPlan>('/api/plan/current'),
    // The editor keeps its own draft; a background refetch mid-edit would
    // only fight it.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

// Every write returns the whole current plan, recomputed, so each one
// simply replaces the cached copy.
function useCurrentPlanWrite<T>(request: (body: T) => Promise<CurrentPlan>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: (data) => queryClient.setQueryData(planKeys.current, data),
  })
}

export const useSavePlan = () => useCurrentPlanWrite((body: SavePlanInput) => api.put<CurrentPlan>('/api/plan/current', body))
export const useSaveLoad = () => useCurrentPlanWrite((body: LoadDataInput) => api.put<CurrentPlan>('/api/plan/load', body))
export const useSetCruise = () =>
  useCurrentPlanWrite(({ registration, cruiseSpeed }: { registration: string; cruiseSpeed: number | null }) =>
    api.put<CurrentPlan>(`/api/plan/cruise-speed/${registration}`, { cruiseSpeed }),
  )

export function useLoadRoute() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (routeId: number) => api.post<CurrentPlan>(`/api/plan/routes/${routeId}/load`, {}),
    onSuccess: (data) => queryClient.setQueryData(planKeys.current, data),
  })
}

export function useSaveRoute() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (name: string) => api.post<{ routeId: number }>('/api/plan/routes', { name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: planKeys.routes }),
  })
}

export function useDeleteRoute() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (routeId: number) => api.delete(`/api/plan/routes/${routeId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: planKeys.routes }),
  })
}

// --- Waypoints ----------------------------------------------------------------

function useWaypointWrite<T>(request: (body: T) => Promise<unknown>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['plan', 'waypoints'] }),
  })
}

export const useCreateWaypoint = () =>
  useWaypointWrite((body: WaypointInput) => api.post<{ waypoint: PlanWaypoint }>('/api/plan/waypoints', body))
export const useUpdateWaypoint = () =>
  // `code` is the waypoint being edited; the body's own code may rename it.
  useWaypointWrite(({ code, ...body }: WaypointInput & { code: string; newCode: string }) =>
    api.put(`/api/plan/waypoints/${encodeURIComponent(code)}`, { ...body, code: body.newCode }),
  )
export const useDeleteWaypoint = () =>
  useWaypointWrite((code: string) => api.delete(`/api/plan/waypoints/${encodeURIComponent(code)}`))
export const useShareWaypoint = () =>
  useWaypointWrite(({ code, isShared }: { code: string; isShared: boolean }) =>
    api.put(`/api/plan/waypoints/${encodeURIComponent(code)}/shared`, { isShared }),
  )
